import classnames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Badge, Button } from '@scramble/ui';
import { APP_LINKS } from '@/helpers/links';
import { prettyDate, prettyFloatMoney } from '@/helpers/utils';
import { MyLink } from '@/components/MyLink';
import { Preloader } from '@/components/Preloader';
import { useConfirmedAllocationStatus } from '@/hooks/shared/useConfirmedAllocationStatus';
import { useGetFundraisingDates } from '@/query/queries/investing/useGetFundraisingDates';
import { useRoundStatus } from '@/hooks/shared/useRoundStatus';
import { useGetBalance } from '@/query/queries/cash/useGetBalance';
import IconAdd from '@scramble/design-system/icons/plus-circle.svg?react';
import IconArrow from '@scramble/design-system/icons/arrow-circle-right.svg?react';
import s from './index.module.scss';

export const RoundInfo = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { status, isRoundProcessing, minInvestAmount } = useRoundStatus();
  const { data: fundraisingDates } = useGetFundraisingDates();
  const { data: balance } = useGetBalance();
  const { data: allocatedInfo, totalAllocated } = useConfirmedAllocationStatus();

  const isLoaded = !!(status && balance && fundraisingDates && allocatedInfo);

  if (!isLoaded)
    return (
      <div className={s.wrapper}>
        <Preloader />
      </div>
    );

  const isActive = fundraisingDates.is_active;
  const period = `${prettyDate(fundraisingDates.start_date, 'D')}-${prettyDate(fundraisingDates.end_date, 'D MMMM')}`;
  const freeCash = Number(balance.available || 0);
  const bonusAmount = Number(balance.available_bonus_amount || 0);
  const investedAmount = Number(balance.on_hold || 0);
  const totalAvailableAmount = freeCash + bonusAmount;

  const canJoin =
    status &&
    isActive &&
    freeCash + bonusAmount + investedAmount >= minInvestAmount;

  const showMinInvestAmount =
    !totalAllocated && totalAvailableAmount < minInvestAmount;

  return (
    <div className={s.wrapper}>
      <div className={s.content}>
        <>
          {isRoundProcessing ? (
            <Badge
              label={t('dashboard:round_info.round_processing')}
              dot
              type="neutral"
              size="medium"
            />
          ) : isActive ? (
            <Badge
              label={t('dashboard:round_info.round_active')}
              dot
              type="success"
              size="medium"
            />
          ) : (
            <Badge
              label={t('dashboard:round_info.round_coming')}
              dot
              type="neutral"
              size="medium"
            />
          )}

          <div className={s.title}>
            {isRoundProcessing
              ? t('dashboard:round_info.finalizing', { period })
              : totalAllocated
                ? t('dashboard:round_info.invest_more', { period })
                : isActive
                  ? t('dashboard:round_info.join_now', { period })
                  : t('dashboard:round_info.get_ready', { period })}
          </div>

          <div className={s.description}>
            {isRoundProcessing ? (
              t('dashboard:round_info.round_is_processing')
            ) : (
              <>
                {isActive &&
                  (totalAllocated ? (
                    <Trans
                      ns="dashboard"
                      i18nKey="round_info.allocated_invest_more"
                      values={{ amount: prettyFloatMoney(totalAllocated) }}
                      components={{
                        LinkInvest: (
                          <MyLink href={APP_LINKS.investing} underline />
                        ),
                      }}
                    />
                  ) : (
                    t('dashboard:round_info.join_now_and_start_investing')
                  ))}

                {!isActive &&
                  (showMinInvestAmount
                    ? t('dashboard:round_info.minimum_investment', {
                        amount: prettyFloatMoney(status.min_investment_amount),
                      })
                    : t('dashboard:round_info.wait_next_round')
                  )}
              </>
            )}
          </div>
        </>
      </div>

      <div className={s.buttons}>
        <Button
          theme="neutral"
          appearance="filled"
          size="lg"
          onClick={() => navigate(APP_LINKS.addFunds)}
          className={s.btn_add_cash}
        >
          <IconAdd />
          <span className="f-body-md f-500">
            {t('dashboard:round_info.add_cash')}
          </span>
        </Button>
        <Button
          theme="brand"
          appearance="filled"
          size="lg"
          onClick={() =>
            navigate(APP_LINKS.investing, {
              state: { invest: !!totalAllocated },
            })
          }
          disabled={!canJoin}
          className={s.btn_join}
        >
          <IconArrow
            className={classnames(s.icon_arrow, !canJoin && 'disabled')}
          />
          <span className="f-body-md f-500">
            {isRoundProcessing || !totalAllocated
              ? t('dashboard:round_info.join_button')
              : t('dashboard:round_info.invest_more_button')}
          </span>
        </Button>
      </div>
    </div>
  );
};
